import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronUp,
  Printer,
  Save,
  Settings2,
  Trash2,
  Upload,
} from 'lucide-react'

import { apiConfig } from '../config/authConfig'
import { apiClient } from '../services/api'
import { usePermissions } from '../hooks/usePermissions'
import AiHelpChat from '../components/AiHelpChat'

// ─── Types ────────────────────────────────────────────────────────────────────

interface HelpSection {
  section_key: string
  locale: string
  title: string
  content: string
  sort_order: number
  updated_by: string | null
}

// Map section_key → permission key from usePermissions().
// null = visible to everyone, undefined (unknown key) = also visible to everyone.
type PermissionKey = keyof Omit<ReturnType<typeof usePermissions>, 'isLoading'>
const SECTION_MODULE_MAP: Record<string, PermissionKey | null> = {
  intro:                null,
  navigation:           null,
  dashboard:            null,
  settings:             null,
  invoices:             'canAccessInvoices',
  reports:              'canAccessReports',
  ambulance:            'canAccessAmbulance',
  costCenters:          'isAdmin',
  users:                'isAdmin',
  audit:                'isAdmin',
  organizations:        'isSystemAdmin',
  paymentTraffic:       'isSystemAdmin',
  securityGroups:       'isSystemAdmin',
  screenshotManagement: 'isSystemAdmin',
}

// ─── Screenshot slots ─────────────────────────────────────────────────────────

const SCREENSHOT_SLOTS = [
  { name: 'navigation', label: 'Navigointi' },
  { name: 'dashboard', label: 'Kojelauta' },
  { name: 'invoices-list', label: 'Laskujen listaus' },
  { name: 'invoice-detail', label: 'Laskun tiedot' },
  { name: 'invoice-inspection', label: 'Laskun tarkastus' },
  { name: 'cost-centers', label: 'Kustannuspaikat' },
  { name: 'cost-center-permissions', label: 'Kustannuspaikkaoikeudet' },
  { name: 'users', label: 'Käyttäjähallinta' },
  { name: 'user-invite', label: 'Käyttäjän kutsuminen' },
  { name: 'user-cc-permissions', label: 'Kustannuspaikkaoikeudet (käyttäjältä)' },
  { name: 'ambulance-order', label: 'Ambulanssitilaus' },
  { name: 'ambulance-review', label: 'Ambulanssitarkastus' },
  { name: 'organizations', label: 'Organisaatiot' },
  { name: 'org-edit', label: 'Organisaation muokkaus' },
  { name: 'payment-traffic', label: 'Maksuliikenne' },
  { name: 'security-groups', label: 'Ryhmäoikeudet' },
  { name: 'settings', label: 'Asetukset' },
] as const

// ─── HelpImage ────────────────────────────────────────────────────────────────

function HelpImage({ name }: { name: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) {
    return (
      <span className="inline-block px-2 py-0.5 text-xs font-mono bg-gray-100 text-gray-400 rounded border border-dashed border-gray-300">
        [kuva: {name}]
      </span>
    )
  }
  return (
    <img
      src={`${apiConfig.baseUrl}/help/screenshots/${name}`}
      alt={name}
      className="max-w-full rounded-lg border border-gray-200 shadow-sm my-3"
      onError={() => setFailed(true)}
    />
  )
}

// ─── HelpContent ─────────────────────────────────────────────────────────────
// Renders: \n\n → <p>, ![name] → <HelpImage>

function HelpContent({ text }: { text: string }) {
  // Split by screenshot tags, keeping the tags in the array
  const parts = text.split(/(!\[[^\]]+\])/)
  return (
    <>
      {parts.map((part, i) => {
        const imgMatch = part.match(/^!\[([^\]]+)\]$/)
        if (imgMatch) {
          return <HelpImage key={i} name={imgMatch[1]} />
        }
        return part
          .split('\n\n')
          .filter(Boolean)
          .map((paragraph, j) => (
            <p key={`${i}-${j}`} className="mb-4 text-gray-700 leading-relaxed">
              {paragraph}
            </p>
          ))
      })}
    </>
  )
}

// ─── HelpPage ─────────────────────────────────────────────────────────────────

export default function HelpPage() {
  const { t, i18n } = useTranslation('help')
  const { t: tc } = useTranslation('common')
  const locale = i18n.language.startsWith('fi') ? 'fi' : 'en'
  const location = useLocation()
  const perms = usePermissions()
  const { isSystemAdmin } = perms
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})

  const { data: allSections = [], isLoading } = useQuery<HelpSection[]>({
    queryKey: ['help-content', locale],
    queryFn: () => apiClient.get(`/help/content?locale=${locale}`),
    staleTime: 5 * 60 * 1000,
  })

  // Filter sections based on module permissions
  const visibleSections = allSections.filter((s) => {
    const requiredPerm = SECTION_MODULE_MAP[s.section_key]
    if (requiredPerm === undefined || requiredPerm === null) return true
    return !!perms[requiredPerm]
  })

  // Scroll to section from URL hash
  useEffect(() => {
    if (!location.hash || visibleSections.length === 0) return
    const key = location.hash.slice(1)
    const el = sectionRefs.current[key]
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    }
  }, [location.hash, visibleSections.length])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        {tc('loading')}
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('subtitle')}</p>
        </div>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Printer className="w-4 h-4" />
          {t('print')}
        </button>
      </div>

      {/* Print title */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold">Tilaajakanava — {t('title')}</h1>
      </div>

      {/* Table of contents */}
      {visibleSections.length > 0 && (
        <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200 print:hidden">
          <h2 className="font-semibold text-gray-600 mb-3 text-xs uppercase tracking-wider">
            {t('tableOfContents')}
          </h2>
          <ol className="list-decimal list-inside space-y-1 columns-2">
            {visibleSections.map((s) => (
              <li key={s.section_key} className="break-inside-avoid">
                <button
                  className="text-primary-700 hover:underline text-sm text-left"
                  onClick={() => {
                    sectionRefs.current[s.section_key]?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    })
                  }}
                >
                  {s.title}
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Content sections */}
      {visibleSections.length === 0 ? (
        <div className="text-center py-16 text-gray-400">{t('noContent')}</div>
      ) : (
        <div className="space-y-12">
          {visibleSections.map((s) => (
            <section
              key={s.section_key}
              id={s.section_key}
              ref={(el) => {
                sectionRefs.current[s.section_key] = el
              }}
              className="scroll-mt-24"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                {s.title}
              </h2>
              <HelpContent text={s.content} />
            </section>
          ))}
        </div>
      )}

      {/* Admin panel — sys_admin only */}
      {isSystemAdmin && (
        <AdminPanel
          allSections={allSections}
          locale={locale}
        />
      )}
    </div>
  )
}

// ─── AdminPanel ───────────────────────────────────────────────────────────────

interface AdminPanelProps {
  allSections: HelpSection[]
  locale: string
}

function AdminPanel({ allSections, locale }: AdminPanelProps) {
  const { t } = useTranslation('help')
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'content' | 'screenshots' | 'ai'>('content')
  const [queryLocale, setQueryLocale] = useState<'fi' | 'en'>(
    locale === 'fi' ? 'fi' : 'en',
  )
  const [aiEnabled, setAiEnabled] = useState(false)

  // Fetch sections for the editor locale (may differ from page locale)
  const { data: editorSections = [], refetch } = useQuery<HelpSection[]>({
    queryKey: ['help-content', queryLocale],
    queryFn: () => apiClient.get(`/help/content?locale=${queryLocale}`),
    staleTime: 0,
    enabled: open && tab === 'content',
  })

  // Check if AI is enabled
  useEffect(() => {
    apiClient
      .get<{ enabled: boolean }>('/help/ai/status')
      .then((res) => setAiEnabled(res.enabled))
      .catch(() => setAiEnabled(false))
  }, [])

  const tabs = [
    { key: 'content' as const, label: t('contentEditor') },
    { key: 'screenshots' as const, label: t('screenshotManager') },
    ...(aiEnabled ? [{ key: 'ai' as const, label: t('aiAssistant') }] : []),
  ]

  return (
    <div className="mt-20 border-t-2 border-dashed border-gray-300 pt-6 print:hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
      >
        <Settings2 className="w-4 h-4" />
        {t('adminPanel')}
        {open ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {open && (
        <div className="mt-5">
          {/* Tab bar */}
          <div className="flex gap-0 mb-5 border-b border-gray-200">
            {tabs.map((tabItem) => (
              <button
                key={tabItem.key}
                onClick={() => setTab(tabItem.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === tabItem.key
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tabItem.label}
              </button>
            ))}
          </div>

          {tab === 'content' && (
            <ContentEditor
              editorLocale={queryLocale}
              setEditorLocale={setQueryLocale}
              editorSections={editorSections}
              allSections={allSections}
              onSaved={() => refetch()}
            />
          )}
          {tab === 'screenshots' && <ScreenshotManager />}
          {/* AiHelpChat stays mounted to preserve conversation across tab switches */}
          {aiEnabled && (
            <div className={tab === 'ai' ? '' : 'hidden'}>
              <AiHelpChat onContentSaved={() => refetch()} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── ContentEditor ────────────────────────────────────────────────────────────

interface ContentEditorProps {
  editorLocale: 'fi' | 'en'
  setEditorLocale: (lc: 'fi' | 'en') => void
  editorSections: HelpSection[]
  allSections: HelpSection[]
  onSaved: () => void
}

function ContentEditor({
  editorLocale,
  setEditorLocale,
  editorSections,
  allSections,
  onSaved,
}: ContentEditorProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  // Build map for current editor locale
  const sectionMap: Record<string, HelpSection> = {}
  editorSections.forEach((s) => {
    sectionMap[s.section_key] = s
  })

  // Unique section keys sorted by sort_order
  const allKeys = Array.from(new Set(allSections.map((s) => s.section_key))).sort(
    (a, b) => {
      const ao = allSections.find((s) => s.section_key === a)?.sort_order ?? 99
      const bo = allSections.find((s) => s.section_key === b)?.sort_order ?? 99
      return ao - bo
    },
  )

  return (
    <div>
      {/* Locale selector */}
      <div className="flex gap-2 mb-4">
        {(['fi', 'en'] as const).map((lc) => (
          <button
            key={lc}
            onClick={() => setEditorLocale(lc)}
            className={`px-4 py-1.5 text-sm rounded-full font-medium transition-colors ${
              editorLocale === lc
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {lc === 'fi' ? 'Suomi' : 'English'}
          </button>
        ))}
      </div>

      {/* Section accordion */}
      <div className="space-y-2">
        {allKeys.map((key) => {
          const s = sectionMap[key]
          const isExpanded = expandedKey === key
          return (
            <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedKey(isExpanded ? null : key)}
                className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-800">
                  {s?.title || key}
                  <span className="ml-2 text-xs text-gray-400 font-mono">({key})</span>
                </span>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
              </button>
              {isExpanded && (
                <SectionForm
                  sectionKey={key}
                  locale={editorLocale}
                  initial={s ?? null}
                  onSaved={onSaved}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── SectionForm ──────────────────────────────────────────────────────────────

interface SectionFormProps {
  sectionKey: string
  locale: 'fi' | 'en'
  initial: HelpSection | null
  onSaved: () => void
}

function SectionForm({ sectionKey, locale, initial, onSaved }: SectionFormProps) {
  const { t } = useTranslation('help')
  const { t: tc } = useTranslation('common')
  const [title, setTitle] = useState(initial?.title ?? '')
  const [content, setContent] = useState(initial?.content ?? '')
  const [savedOk, setSavedOk] = useState(false)

  // Reset when initial changes (locale switch opens different data)
  useEffect(() => {
    setTitle(initial?.title ?? '')
    setContent(initial?.content ?? '')
    setSavedOk(false)
  }, [initial])

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.put(`/help/content/${sectionKey}/${locale}`, {
        title,
        content,
        sort_order: initial?.sort_order ?? 0,
        is_active: true,
      }),
    onSuccess: () => {
      setSavedOk(true)
      onSaved()
      setTimeout(() => setSavedOk(false), 3000)
    },
  })

  return (
    <div className="p-4 space-y-4 bg-white">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('fieldTitle')}
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('fieldContent')}{' '}
          <span className="text-gray-400 font-normal">
            (\n\n = kappale, ![nimi] = kuva)
          </span>
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={10}
          className="w-full px-3 py-2 text-xs font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 leading-relaxed"
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" />
          {saveMutation.isPending ? tc('saving') : tc('save')}
        </button>
        {savedOk && (
          <span className="text-sm text-green-600 font-medium">{t('saved')}</span>
        )}
        {saveMutation.isError && (
          <span className="text-sm text-red-600">{tc('errors.generic')}</span>
        )}
      </div>
    </div>
  )
}

// ─── ScreenshotManager ────────────────────────────────────────────────────────

function ScreenshotManager() {
  const { t } = useTranslation('help')
  const { t: tc } = useTranslation('common')
  const [uploading, setUploading] = useState<Record<string, boolean>>({})
  const [deleting, setDeleting] = useState<Record<string, boolean>>({})
  const [imgLoaded, setImgLoaded] = useState<Record<string, boolean>>({})
  const [imgFailed, setImgFailed] = useState<Record<string, boolean>>({})
  const [imgVersion, setImgVersion] = useState<Record<string, number>>({})
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const baseUrl = apiConfig.baseUrl

  const reloadImg = (name: string) => {
    setImgLoaded((p) => ({ ...p, [name]: false }))
    setImgFailed((p) => ({ ...p, [name]: false }))
    setImgVersion((p) => ({ ...p, [name]: (p[name] ?? 0) + 1 }))
  }

  const handleUpload = async (name: string, file: File) => {
    setUploading((p) => ({ ...p, [name]: true }))
    try {
      const token = await apiClient.getToken()
      const form = new FormData()
      form.append('file', file, file.name)
      const res = await fetch(`${baseUrl}/help/screenshots/${name}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!res.ok) throw new Error('Upload failed')
      reloadImg(name)
    } catch (err) {
      console.error('Screenshot upload failed:', err)
    } finally {
      setUploading((p) => ({ ...p, [name]: false }))
    }
  }

  const handleDelete = async (name: string) => {
    if (!window.confirm(t('confirmDeleteScreenshot'))) return
    setDeleting((p) => ({ ...p, [name]: true }))
    try {
      await apiClient.delete(`/help/screenshots/${name}`)
      setImgFailed((p) => ({ ...p, [name]: true }))
      setImgLoaded((p) => ({ ...p, [name]: false }))
    } catch (err) {
      console.error('Screenshot delete failed:', err)
    } finally {
      setDeleting((p) => ({ ...p, [name]: false }))
    }
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">{t('screenshotInstructions')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SCREENSHOT_SLOTS.map(({ name, label }) => {
          const version = imgVersion[name] ?? 0
          const loaded = imgLoaded[name] === true
          const failed = imgFailed[name] === true

          return (
            <div key={name} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-700 truncate mr-1">
                  {label}
                </span>
                <span className="text-xs text-gray-400 font-mono flex-shrink-0">{name}</span>
              </div>

              {/* Image preview area */}
              <div className="mb-2 h-20 flex items-center justify-center bg-white rounded border border-gray-200 overflow-hidden">
                {!failed && (
                  <>
                    <img
                      key={`${name}-v${version}`}
                      src={`${baseUrl}/help/screenshots/${name}`}
                      alt={name}
                      className={`max-h-full max-w-full object-contain ${loaded ? 'block' : 'hidden'}`}
                      onLoad={() => setImgLoaded((p) => ({ ...p, [name]: true }))}
                      onError={() => setImgFailed((p) => ({ ...p, [name]: true }))}
                    />
                    {!loaded && (
                      <span className="text-xs text-gray-300">{tc('loading')}</span>
                    )}
                  </>
                )}
                {failed && (
                  <span className="text-xs text-gray-400">{t('noScreenshot')}</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <input
                  ref={(el) => {
                    fileInputRefs.current[name] = el
                  }}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleUpload(name, file)
                    e.target.value = ''
                  }}
                />
                <button
                  onClick={() => fileInputRefs.current[name]?.click()}
                  disabled={uploading[name]}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 transition-colors flex-1 justify-center"
                >
                  <Upload className="w-3 h-3" />
                  {uploading[name] ? '...' : t('upload')}
                </button>
                {loaded && (
                  <button
                    onClick={() => handleDelete(name)}
                    disabled={deleting[name]}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    {deleting[name] ? '...' : tc('delete')}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
